<script setup>
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import AudioToggle from './AudioToggle.vue';
import AudioAlertEvent from './AudioAlertEvent.vue';
import AudioAlertCondition from './AudioAlertCondition.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import { initializeAudioAlerts } from 'dashboard/helper/scriptHelpers';
import { useStoreGetters } from 'dashboard/composables/store';

const store = useStore();
const getters = useStoreGetters();
const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();

const audioEnabled = ref(false);
const audioAlert = ref('');
const playAudioWhenTabIsInactive = ref(false);
const alertIfUnreadConversationExist = ref(false);
const audioAlertConditions = ref([]);

const i18nKeyPrefix = 'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION';

const currentUser = computed(() => getters.getCurrentUser.value);

const initializeNotificationUISettings = (newUISettings) => {
  const updatedUISettings = camelcaseKeys(newUISettings);

  audioEnabled.value = updatedUISettings.enableAudioAlerts || false;

  audioAlert.value = updatedUISettings.enableAudioAlerts || 'none';

  playAudioWhenTabIsInactive.value = !updatedUISettings.alwaysPlayAudioAlert;
  alertIfUnreadConversationExist.value =
    updatedUISettings.alertIfUnreadAssignedConversationExist;
  audioAlertConditions.value = [
    {
      id: 'audio1',
      label: t(`${i18nKeyPrefix}.CONDITIONS.CONDITION_ONE`),
      model: playAudioWhenTabIsInactive.value,
      value: 'tab_is_inactive',
    },
    {
      id: 'audio2',
      label: t(`${i18nKeyPrefix}.CONDITIONS.CONDITION_TWO`),
      model: alertIfUnreadConversationExist.value,
      value: 'conversations_are_read',
    },
  ];
};

const handleAudioConfigChange = (value) => {
  updateUISettings(value);
  initializeAudioAlerts(currentUser.value);
  useAlert(t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'), {type: 'success'});
};

const handleAudioToggle = (value) => {
  audioEnabled.value = value;
  if (value) {
    audioAlert.value = 'assigned';
  } else {
    audioAlert.value = 'none';
  }
  handleAudioConfigChange({
    enable_audio_alerts: value,
  });
};
const handleAudioAlertChange = (value) => {
  audioAlert.value = value;
  handleAudioConfigChange({
    enable_audio_alerts: value,
  });
};
const handleAudioAlertConditions = (id, value) => {
  if (id === 'tab_is_inactive') {
    handleAudioConfigChange({
      always_play_audio_alert: !value,
    });
  } else if (id === 'conversations_are_read') {
    handleAudioConfigChange({
      alert_if_unread_assigned_conversation_exist: value,
    });
  }
};
onMounted(() => {
  store.dispatch('userNotificationSettings/get');
});
watch(
  uiSettings,
  (value) => {
    initializeNotificationUISettings(value);
  },
  { immediate: true }
);
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-6">
    <AudioToggle :value="audioEnabled" :label="$t(`${i18nKeyPrefix}.TITLE`)" @update="handleAudioToggle" />

    <AudioAlertEvent :label="$t(`${i18nKeyPrefix}.ALERT_TYPE.TITLE`)" :value="audioAlert"
      @update="handleAudioAlertChange" />

    <AudioAlertCondition :items="audioAlertConditions" :label="$t(`${i18nKeyPrefix}.CONDITIONS.TITLE`)"
      @change="handleAudioAlertConditions" />
  </div>
</template>
